export interface LeaderboardModeTab {
  id: 'casual' | 'ranked'
  label: string
}

export interface LeaderboardEntryRow {
  rank: number
  medal: string
  name: string
  points: string
  wins: string
  tierName: string
  tierColor: string
  isMe: boolean
}

export interface LeaderboardOverlayOptions {
  title: string
  pitch: string
  loadingText: string
  backLabel: string
  modes: LeaderboardModeTab[]
  activeMode: 'casual' | 'ranked'
  onMode: (mode: 'casual' | 'ranked') => void
  onBack: () => void
}

export interface LeaderboardOverlayHandle {
  setMode: (mode: 'casual' | 'ranked') => void
  setStatus: (text: string, tone?: 'bad' | 'sub') => void
  render: (seasonLine: string, rows: LeaderboardEntryRow[], meLine: string) => void
  destroy: () => void
}

export function mountLeaderboardOverlay(options: LeaderboardOverlayOptions): LeaderboardOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-leaderboard-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-leaderboard-panel'

  const header = document.createElement('header')
  header.className = 'game-leaderboard-header'
  const title = document.createElement('h1')
  title.textContent = options.title
  const pitch = document.createElement('p')
  pitch.textContent = options.pitch
  header.append(title, pitch)

  const tabs = document.createElement('div')
  tabs.className = 'game-leaderboard-tabs'

  const tabButtons = new Map<string, HTMLButtonElement>()
  for (const mode of options.modes) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'game-leaderboard-tab'
    btn.textContent = mode.label
    btn.addEventListener('click', () => options.onMode(mode.id))
    tabs.append(btn)
    tabButtons.set(mode.id, btn)
  }

  const status = document.createElement('p')
  status.className = 'game-leaderboard-status'

  const season = document.createElement('p')
  season.className = 'game-leaderboard-season'

  const list = document.createElement('div')
  list.className = 'game-leaderboard-list'

  const me = document.createElement('p')
  me.className = 'game-leaderboard-me'

  const back = document.createElement('button')
  back.type = 'button'
  back.className = 'game-leaderboard-btn'
  back.textContent = options.backLabel
  back.addEventListener('click', options.onBack)

  panel.append(header, tabs, status, season, list, me, back)
  root.append(panel)
  document.body.append(root)

  const setMode = (mode: 'casual' | 'ranked'): void => {
    for (const [id, btn] of tabButtons) btn.dataset.active = id === mode ? '1' : '0'
  }

  const setStatus = (text: string, tone: 'bad' | 'sub' = 'sub'): void => {
    status.textContent = text
    status.hidden = !text
    status.dataset.tone = tone
  }

  setMode(options.activeMode)
  setStatus(options.loadingText)

  return {
    setMode,
    setStatus,
    render: (seasonLine, rows, meLine) => {
      setStatus('')
      season.textContent = seasonLine
      season.hidden = !seasonLine
      list.innerHTML = ''
      for (const row of rows) {
        const card = document.createElement('article')
        card.className = `game-leaderboard-row ${row.isMe ? 'is-me' : ''}`
        card.style.setProperty('--tier', row.tierColor || '#9aa4b2')
        card.innerHTML = [
          `<b>${escapeHtml(row.medal)}</b>`,
          '<span>',
          `<strong>${escapeHtml(row.name)}</strong>`,
          `<em>${escapeHtml([row.wins, row.tierName].filter(Boolean).join(' · '))}</em>`,
          '</span>',
          `<i>${escapeHtml(row.points)}</i>`,
        ].join('')
        list.append(card)
      }
      me.textContent = meLine
      me.hidden = !meLine
    },
    destroy: () => root.remove(),
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
